/**
 * Privacy Policy — public static page.
 * Renders without onboarding or auth gates so app-store reviewers and search
 * engines can reach it directly at /privacy. Keep the URL stable.
 */
export default function Privacy() {
  return (
    <div className="min-h-screen bg-[#0F0F0F] text-white" data-testid="privacy-page">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <a
          href="/"
          className="inline-block text-xs font-bold tracking-[0.2em] uppercase text-[#71717A] hover:text-[#E05A1D] transition-colors mb-8"
          data-testid="privacy-back-link"
        >
          ← Back to UpTop
        </a>

        <h1
          className="text-4xl sm:text-5xl font-black tracking-tighter uppercase mb-2"
          style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
        >
          <span className="text-[#E05A1D]">UP</span>TOP
        </h1>
        <h2 className="text-2xl font-bold tracking-tight mb-8 text-white/90">Privacy Policy</h2>
        <p className="text-xs text-[#71717A] uppercase tracking-widest mb-12">
          Last updated: February 2026
        </p>

        <Section title="1. Who we are">
          <p>
            UpTop is a personal fitness tracking app maintained by an independent developer.
            The app stores your workout, body and nutrition data so you can review your
            progress over time.
          </p>
        </Section>

        <Section title="2. Data we collect">
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong>Profile:</strong> name, gender, age, body weight, height and (optionally)
              your primary fitness goal — entered by you during onboarding.
            </li>
            <li>
              <strong>Workouts:</strong> exercises, sets, reps, weights, durations, notes and
              timestamps you log inside the app.
            </li>
            <li>
              <strong>Body measurements:</strong> values you record on the Body page (weight,
              body fat, circumferences) and the dates you record them.
            </li>
            <li>
              <strong>Nutrition:</strong> meals and macros you log on the Calories page,
              including photos you optionally upload for the food scanner.
            </li>
            <li>
              <strong>Schedule, plans, favorites and personal records</strong> derived from
              your activity inside the app.
            </li>
            <li>
              <strong>Email address</strong> only if you choose to link one for weekly recaps
              or multi-device sync (Settings → Email Account). Linking is optional.
            </li>
            <li>
              <strong>Push subscription</strong> tokens if you opt into reminders in Settings.
            </li>
          </ul>
          <p className="mt-3">
            We do <strong>not</strong> collect advertising identifiers, contacts, precise
            location, microphone, SMS, or call logs. We do not run third-party advertising
            SDKs.
          </p>
        </Section>

        <Section title="3. How we use your data">
          <ul className="list-disc pl-6 space-y-2">
            <li>To display your dashboard, charts, streaks and personal records.</li>
            <li>To compute recommended calories and AI-generated workout suggestions tailored to your profile.</li>
            <li>To send the weekly email recap (only if you explicitly opt in).</li>
            <li>To deliver push notifications you have explicitly enabled.</li>
            <li>To sync your data across devices when you link the same verified email.</li>
          </ul>
          <p className="mt-3">
            We do not sell your data and we do not share it with third-party advertisers.
          </p>
        </Section>

        <Section title="4. Third-party processors">
          <p>UpTop relies on the following processors to function:</p>
          <ul className="list-disc pl-6 space-y-2 mt-2">
            <li>
              <strong>MongoDB Atlas</strong> — stores your account data at rest.
            </li>
            <li>
              <strong>OpenAI</strong> — powers the AI coach, plan generator, food-photo
              analysis and recommendations. Text and (optionally) the food photo you scan are
              sent to OpenAI's API. Photos are not stored on our servers beyond the request.
            </li>
            <li>
              <strong>Resend</strong> — delivers verification codes and the weekly recap email
              when you opt in.
            </li>
            <li>
              <strong>wger.de</strong> &amp; <strong>Wikipedia</strong> — public APIs we query
              to fetch exercise demos and images. No personal data is sent.
            </li>
          </ul>
        </Section>

        <Section title="5. Retention">
          <p>
            We keep your data for as long as your account is active. You can delete every
            piece of data we hold about you at any time from Settings → Delete Account, which
            calls our <code className="text-[#E05A1D]">DELETE /api/account</code> endpoint and
            removes every record tagged to your user id from our database. The deletion is
            permanent and cannot be undone.
          </p>
        </Section>

        <Section title="6. Children">
          <p>
            UpTop is not directed to children under 13 and we do not knowingly collect data
            from them.
          </p>
        </Section>

        <Section title="7. Your rights">
          <p>
            You can export every workout you have logged at any time via the "Export CSV"
            button on the Workouts page. You can delete your account in one tap from
            Settings. If you have any other request regarding your data, contact us at
            the address below.
          </p>
        </Section>

        <Section title="8. Contact">
          <p>
            Questions about this policy? Email{" "}
            <a
              href="mailto:hello@uptop.app"
              className="text-[#E05A1D] hover:underline"
            >
              hello@uptop.app
            </a>
            .
          </p>
        </Section>

        <div className="mt-12 pt-6 border-t border-[#27272A] text-xs text-[#71717A]">
          See also our{" "}
          <a href="/terms" className="text-[#E05A1D] hover:underline">
            Terms of Service
          </a>
          .
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-10">
      <h3
        className="text-lg font-bold tracking-tight uppercase mb-3 text-white"
        style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
      >
        {title}
      </h3>
      <div className="text-sm leading-relaxed text-[#A1A1AA] space-y-2">{children}</div>
    </section>
  );
}
